import React from 'react';
import useForm from '~/app/modules/form/useForm';
import { ServiceCompletePayload } from '~/types';
import classes from './style.module.scss';
import MForm from '~/app/modules/form';
import Textarea from '~/app/modules/form/Textarea';
import useTranslation from '~/app/hooks/useTranslation';
import { useWorkOrderHPCodes, useWorkTypes } from '~/app/data/statuses/issue';
import { useStoreState } from '~/store';
import WorkHoursInput from '~/app/modules/form/WorkHoursInput';
import Select from '~/app/modules/form/Select';
import Text from '~/app/modules/form/Text';
import Date from '~/app/modules/form/Date';
import moment from 'moment';

export type DetailsComponent = React.FC;

const Details: DetailsComponent = () => {
  
  const { t } = useTranslation('issue');
  const { values: formValues, fieldEvents: formFieldEvents, errors } = useForm<ServiceCompletePayload>({ formName: 'completeService' });
  const serviceDetails = useStoreState(state => state.service.data);

  const workTypes = useStoreState(state => state.issue.workTypes);
  const workorderTypes = useWorkTypes(workTypes);

  const HPCodes = useStoreState(state => state.issue.HPCodes);
  const issueHpCodes = useWorkOrderHPCodes(HPCodes);
  const isHPCodeRequired = useStoreState(state => state.issue.isHPCodeRequired);
  const hasNextServiceSection = useStoreState(state => state.service.hasNextServiceSection);

  const costCategories = useStoreState(state => state.issue.costCategories);
  const selectedCategory = costCategories.find(({ id }) => Number(id) === Number(serviceDetails?.costCategoryId));

  const overtime1Percentage = serviceDetails?.workOrderCost?.overtime1Percentage ?? selectedCategory?.costData?.overtime1Percentage;
  const overtime2Percentage = serviceDetails?.workOrderCost?.overtime2Percentage ?? selectedCategory?.costData?.overtime2Percentage;

  return (
    <>
      <MForm className={`${classes['form']} ${classes['details']}`}>
        <div className={classes['issue-complete']}>
          <Textarea
            className={`${classes['input-container']}`}
            placeholder={t('issueClose.comment.placeholder')}
            descriptionPosition="top"
            name="comment"
            defaultValue={formValues.comment}
            errors={errors.comment}
            {...formFieldEvents}
          />
          {isHPCodeRequired && <Select
            className={`${classes['input-container']}`}
            name="hpCode"
            placeholder={t('hpCode.placeholder')}
            options={issueHpCodes}
            icon={'keyboard_arrow_down'}
            onChange={formFieldEvents.onChange}
            defaultValue={formValues.hpCode}
            errors={errors.hpCode}
            showEmptyOption={true}
            emptyOptionValue={0}
          />}
          {hasNextServiceSection === true && (
            <div className={classes['next-service-container']}>
              <div className={classes['next-service-wrapper']}>
                <div>{t('nextServicePerformedAt')}</div>
                {serviceDetails?.primaryMeterUnit !== null && 
                  <Text
                    placeholder={t('nextDynamicServiceOccurrence.placeholder')}
                    name="nextDynamicServiceOccurrence"
                    defaultValue={formValues.nextDynamicServiceOccurrence}
                    type='number'
                    className={classes['next-service']}
                    hasError={errors?.nextDynamicServiceOccurrence !== undefined}
                    errorMessage={t('nextDynamicServiceOccurrence.errorMin', { min: serviceDetails.operatingTime })}
                    errors={errors.nextDynamicServiceOccurrence}
                    {...formFieldEvents}
                  />
                }
                {serviceDetails?.scheduleAt !== null && 
                  <Date
                    placeholder={t('nextDynamicScheduleAt.placeholder')}
                    name="nextDynamicScheduleAt"
                    defaultValue={formValues.nextDynamicScheduleAt}
                    className={classes['next-service']}
                    format={'DD.MM.YYYY'} type='date'
                    min={moment().format('YYYY-MM-DD')}
                    errors={errors.nextDynamicScheduleAt}
                    {...formFieldEvents}
                  />
                }
              </div>
              <div className={classes['next-service-wrapper']}>
                <div>{t('noticeBeforeTheNextService')}</div>
                {serviceDetails?.primaryMeterUnit !== null && 
                  <Text
                    placeholder={t('nextAlertServiceOccurrence.placeholder')}
                    name="nextAlertServiceOccurrence"
                    defaultValue={formValues.nextAlertServiceOccurrence}
                    type='number'
                    className={classes['next-service']}
                    hasError={errors?.nextAlertServiceOccurrence !== undefined}
                    errorMessage={t('nextAlertServiceOccurrence.errorBetween', { min: 1, max: (Number(serviceDetails.primaryMeterUnit) - 1) })}
                    errors={errors.nextAlertServiceOccurrence}
                    {...formFieldEvents}
                  />
                }
                {serviceDetails?.scheduleAt !== null && 
                  <Text
                    placeholder={t('nextAlertScheduleDays.placeholder')}
                    name="nextAlertScheduleDays"
                    defaultValue={formValues.nextAlertScheduleDays}
                    type='number'
                    className={classes['next-service']}
                    hasError={errors?.nextAlertScheduleDays !== undefined}
                    errorMessage={t('nextAlertScheduleDays.errorMin', { min: 1 })}
                    errors={errors.nextAlertScheduleDays}
                    {...formFieldEvents}
                  />
                }
              </div>
            </div>
          )}
          <WorkHoursInput
            placeholder={t('ordinaryHours.placeholder')}
            name="ordinaryHours"
            defaultValue={formValues.ordinaryHours}
            errors={errors.ordinaryHours}
            {...formFieldEvents}
          />
          <WorkHoursInput
            placeholder={overtime1Percentage
              ? t('overTime50.placeholder', { overTime50: overtime1Percentage })
              : t('overTime50.defaultPlaceholder')
            }
            name="overTime50"
            defaultValue={formValues.overTime50}
            errors={errors.overTime50}
            {...formFieldEvents}
          />
          <WorkHoursInput
            placeholder={overtime2Percentage
              ? t('overTime100.placeholder', { overTime100: overtime2Percentage })
              : t('overTime100.defaultPlaceholder')
            }
            name="overTime100"
            defaultValue={formValues.overTime100}
            errors={errors.overTime100}
            {...formFieldEvents}
          />
          <Select
            className={`${classes['input-container']}`}
            name="workType"
            placeholder={t('workType.placeholder')}
            options={workorderTypes}
            icon={'keyboard_arrow_down'}
            onChange={formFieldEvents.onChange}
            defaultValue={formValues.workType}
            showEmptyOption={true}
            emptyOptionValue={0}
            errors={errors.workType}
          />
        </div>
      </MForm>
    </>
  );
};

export default Details;
